import { useEffect, useMemo, useState } from 'react'
import { ReservedResourceF } from '../../../../../types/reservation-detail.types'
import { compareDates, getNightsInRange } from 'src/utils/date'
import { ChargeCategoryFragment } from 'src/api/api.generated'

export type ChargeCategoriesPerDay =
  | {
      day: Date
      chargeCategoryId?: string
      initialChargeCategoryId: string
    }
  | undefined

export type RRBulkEditCCGridRow = {
  id: string
  reservedResource: ReservedResourceF
  chargeCategoriesPerDay: ChargeCategoriesPerDay[]
}

export type RRBulkEditCCSummaryRow = {
  id: string
  columnsSummaries: {
    day: Date
    summary: { rr: ReservedResourceF; cc?: ChargeCategoryFragment }[]
  }[]
}

export default function useRRBulkEditCCGridRows(start: Date, end: Date, reservedResources: ReservedResourceF[], chargeCategories: ChargeCategoryFragment[]) {
  const [rows, setRows] = useState<RRBulkEditCCGridRow[]>([])
  const [summaryRow, setSummaryRows] = useState<RRBulkEditCCSummaryRow>()

  const days = useMemo(() => getNightsInRange(start, end), [start, end])

  useEffect(() => {
    if (days && days.length > 0) {
      const createChargeCategoryPerDays = (rr: ReservedResourceF): ChargeCategoriesPerDay[] => {
        return days.map((d) => {
          if (compareDates(d, rr.start) >= 0 && compareDates(d, rr.end) < 0) {
            const initialValue = rr.chargeCategories?.find((ccd) => compareDates(ccd.day, d) === 0)?.chargeCategoryId ?? rr.chargeCategory.id
            return {
              day: d,
              chargeCategoryId: initialValue,
              initialChargeCategoryId: initialValue,
            }
          }
          return undefined
        })
      }

      const tmpRows = reservedResources.map((rr) => {
        return {
          id: rr.id,
          reservedResource: rr,
          chargeCategoriesPerDay: createChargeCategoryPerDays(rr),
        }
      })

      setRows(tmpRows)
    }
  }, [days, reservedResources])

  useEffect(() => {
    const tmpSummaryRow: RRBulkEditCCSummaryRow = {
      id: 'summary',
      columnsSummaries: days.map((d) => {
        return {
          day: d,
          summary: [],
        }
      }),
    }

    const updateSummaryOccupancy = (rr: ReservedResourceF, ccd: ChargeCategoriesPerDay) => {
      if (!ccd) return
      const selectedCC = ccd.chargeCategoryId ?? ccd.initialChargeCategoryId
      const indexToUpdate = tmpSummaryRow.columnsSummaries.findIndex((cs) => compareDates(cs.day, ccd.day) === 0)
      const cc = chargeCategories.find((chargeCategory) => chargeCategory.id === selectedCC)
      if (indexToUpdate < 0 && cc) {
        tmpSummaryRow.columnsSummaries[indexToUpdate].summary.push({
          rr: rr,
          cc: cc,
        })
      } else {
        tmpSummaryRow.columnsSummaries.forEach((s) => {
          if (compareDates(s.day, ccd.day) === 0) {
            s.summary.push({ rr: rr, cc: cc })
          }
        })
      }
    }

    rows.forEach((row) => {
      row.chargeCategoriesPerDay.forEach((ccd) => {
        updateSummaryOccupancy(row.reservedResource, ccd)
      })
    })

    setSummaryRows(tmpSummaryRow)
  }, [rows, chargeCategories])

  const updateRow = (rowId: string, day: Date, selectedCCId: string | undefined) => {
    setRows(
      rows.map((r) => {
        if (r.id === rowId)
          return {
            ...r,
            chargeCategoriesPerDay: r.chargeCategoriesPerDay.map((ccd) => {
              if (ccd && ccd.day === day)
                return {
                  ...ccd,
                  chargeCategoryId: selectedCCId,
                }
              return ccd
            }),
          }
        return { ...r }
      }),
    )
  }

  const updateRows = (day: Date, selectedCCId: string | undefined) => {
    setRows(
      rows.map((r) => {
        return {
          ...r,
          chargeCategoriesPerDay: r.chargeCategoriesPerDay.map((ccd) => {
            if (ccd && compareDates(ccd.day, day) === 0) {
              return {
                ...ccd,
                chargeCategoryId: selectedCCId ?? ccd.initialChargeCategoryId,
              }
            }
            return ccd
          }),
        }
      }),
    )
  }

  useEffect(() => {
    console.log('ROWS')
    console.log(rows)
  }, [rows])

  return { days, rows, summaryRow, updateRow, updateRows }
}
